import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo, type JSX, type ReactNode } from 'react';

import { getThemes } from '../api/themesApi';
import { buildTheme } from '../theme';
import { useThemeStore } from '../store/useThemeStore';

/**
 * B19 theme-engine. Builds the MUI theme live from the theme store (user prefs
 * reconciled from `user_settings`; OS media-query fallback). Rebuilds whenever
 * prefs change, so Settings toggles apply without reload. On mount it also
 * fetches the admin-forced global theme (CF-2113) and applies it platform-wide.
 */
export function ThemeController({ children }: { children: ReactNode }): JSX.Element {
  const prefs = useThemeStore((s) => s.prefs);
  const setPrefs = useThemeStore((s) => s.setPrefs);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { global } = await getThemes();
      if (active && global) {
        setPrefs({
          custom: {
            palette: global.palette,
            ...(global.typography ? { typography: global.typography } : {}),
          },
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [setPrefs]);

  const theme = useMemo(
    () => buildTheme(prefs),
    [prefs.mode, prefs.colorblind, prefs.font, prefs.reducedMotion, prefs.custom],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
